import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react"
import Styles from "./index.module.scss"

const setInTransition = innerRefScrollHeight => innerRefScrollHeight !== 0

const renderTriggerSibling = (triggerSibling, classParentString) => {
  if (!triggerSibling) {
    return null
  }

  if (typeof triggerSibling === "string") {
    return (
      <span className={`${classParentString}__trigger-sibling`}>
        {triggerSibling}
      </span>
    )
  }

  if (typeof triggerSibling === "function") {
    return triggerSibling()
  }

  return triggerSibling
}

const LibraryCollapsible = props => {
  const {
    accordionPosition,
    children,
    className = "",
    classParentString = "Collapsible",
    containerElementProps = {},
    contentContainerTagName: ContentContainerElement = "div",
    contentElementId = "",
    contentInnerClassName = "",
    contentOuterClassName = "",
    easing = "linear",
    handleTriggerClick = null,
    lazyRender = false,
    onClose = () => {},
    onClosing = () => {},
    onOpen = () => {},
    onOpening = () => {},
    onTriggerClosing = () => {},
    onTriggerOpening = () => {},
    open: openProp,
    openedClassName = "",
    overflowWhenOpen = "hidden",
    transitionCloseTime = null,
    transitionTime = 400,
    trigger = "",
    triggerClassName = "",
    triggerDisabled = false,
    triggerElementProps = {},
    triggerOpenedClassName = "",
    triggerStyle = null,
    triggerTagName: TriggerElement = "span",
    triggerWhenOpen,
    triggerSibling = null,
    tabIndex,
  } = props
  const isControlled = Object.prototype.hasOwnProperty.call(props, "open")
  const open = Boolean(openProp)
  const instanceId = useId().replace(/:/g, "")
  const timeoutRef = useRef()
  const innerRef = useRef(null)
  const prevOpenRef = useRef(open)
  const prevHeightRef = useRef(open ? "auto" : 0)
  const contentIdRef = useRef(
    contentElementId || `collapsible-content-${instanceId}`
  )
  const triggerIdRef = useRef(
    triggerElementProps.id || `collapsible-trigger-${instanceId}`
  )

  const [state, setState] = useState(() =>
    open
      ? {
          hasBeenOpened: true,
          height: "auto",
          inTransition: false,
          isClosed: false,
          overflow: overflowWhenOpen,
          shouldOpenOnNextCycle: false,
          shouldSwitchAutoOnNextCycle: false,
          transition: "none",
        }
      : {
          hasBeenOpened: false,
          height: 0,
          inTransition: false,
          isClosed: true,
          overflow: "hidden",
          shouldOpenOnNextCycle: false,
          shouldSwitchAutoOnNextCycle: false,
          transition: `height ${transitionTime}ms ${easing}`,
        }
  )

  useEffect(() => {
    return () => {
      window.clearTimeout(timeoutRef.current)
    }
  }, [])

  const continueOpenCollapsible = useCallback(() => {
    const scrollHeight = innerRef.current?.scrollHeight ?? 0

    setState(prevState => ({
      ...prevState,
      hasBeenOpened: true,
      height: scrollHeight,
      inTransition: setInTransition(scrollHeight),
      isClosed: false,
      shouldOpenOnNextCycle: false,
      transition: `height ${transitionTime}ms ${easing}`,
    }))
  }, [easing, transitionTime])

  const openCollapsible = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      inTransition: true,
      shouldOpenOnNextCycle: true,
    }))
  }, [])

  const closeCollapsible = useCallback(() => {
    const scrollHeight = innerRef.current?.scrollHeight ?? 0

    setState(prevState => ({
      ...prevState,
      height: scrollHeight,
      inTransition: setInTransition(scrollHeight),
      shouldSwitchAutoOnNextCycle: true,
      transition: `height ${
        transitionCloseTime || transitionTime
      }ms ${easing}`,
    }))
  }, [easing, transitionCloseTime, transitionTime])

  useEffect(() => {
    if (!state.shouldOpenOnNextCycle) {
      return
    }

    continueOpenCollapsible()
  }, [continueOpenCollapsible, state.shouldOpenOnNextCycle])

  useEffect(() => {
    if (
      (prevHeightRef.current === "auto" || prevHeightRef.current === 0) &&
      state.shouldSwitchAutoOnNextCycle
    ) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => {
        setState(prevState => ({
          ...prevState,
          height: 0,
          isClosed: true,
          overflow: "hidden",
          shouldSwitchAutoOnNextCycle: false,
        }))
      }, 50)
    }

    prevHeightRef.current = state.height
  }, [state.height, state.shouldSwitchAutoOnNextCycle])

  useEffect(() => {
    if (!isControlled || prevOpenRef.current === open) {
      return
    }

    if (open) {
      openCollapsible()
      onOpening()
    } else {
      closeCollapsible()
      onClosing()
    }

    prevOpenRef.current = open
  }, [
    closeCollapsible,
    isControlled,
    onClosing,
    onOpening,
    open,
    openCollapsible,
  ])

  const handleCollapsibleTriggerClick = useCallback(
    event => {
      if (triggerDisabled || state.inTransition) {
        return
      }

      event.preventDefault()

      if (handleTriggerClick) {
        handleTriggerClick(accordionPosition)
        return
      }

      if (isControlled) {
        if (open) {
          onTriggerClosing()
          return
        }

        onTriggerOpening()
        return
      }

      if (state.isClosed) {
        openCollapsible()
        onOpening()
        onTriggerOpening()
        return
      }

      closeCollapsible()
      onClosing()
      onTriggerClosing()
    },
    [
      accordionPosition,
      closeCollapsible,
      handleTriggerClick,
      isControlled,
      onClosing,
      onOpening,
      onTriggerClosing,
      onTriggerOpening,
      open,
      openCollapsible,
      state.inTransition,
      state.isClosed,
      triggerDisabled,
    ]
  )

  const handleTransitionEnd = useCallback(
    event => {
      if (event.target !== innerRef.current) {
        return
      }

      if (!state.isClosed) {
        setState(prevState => ({
          ...prevState,
          height: "auto",
          inTransition: false,
          overflow: overflowWhenOpen,
        }))
        onOpen()
        return
      }

      setState(prevState => ({
        ...prevState,
        inTransition: false,
      }))
      onClose()
    },
    [onClose, onOpen, overflowWhenOpen, state.isClosed]
  )

  const dropdownStyle = {
    WebkitTransition: state.transition,
    height: state.height,
    msTransition: state.transition,
    overflow: state.overflow,
    transition: state.transition,
  }

  const openClass = state.isClosed ? "is-closed" : "is-open"
  const disabledClass = triggerDisabled ? "is-disabled" : ""
  const activeTrigger =
    !state.isClosed && triggerWhenOpen !== undefined
      ? triggerWhenOpen
      : trigger
  const renderedChildren =
    lazyRender &&
    !state.hasBeenOpened &&
    state.isClosed &&
    !state.inTransition
      ? null
      : children
  const isContentHidden = state.isClosed && !state.inTransition

  const triggerClassString = `${classParentString}__trigger ${openClass} ${disabledClass} ${
    state.isClosed ? triggerClassName : triggerOpenedClassName
  }`
  const parentClassString = `${classParentString} ${
    state.isClosed ? className : openedClassName
  }`
  const outerClassString = `${classParentString}__contentOuter ${contentOuterClassName}`
  const innerClassString = `${classParentString}__contentInner ${contentInnerClassName}`

  return (
    <ContentContainerElement
      className={parentClassString.trim()}
      {...containerElementProps}
    >
      <TriggerElement
        id={triggerIdRef.current}
        aria-controls={contentIdRef.current}
        aria-disabled={triggerDisabled}
        aria-expanded={!state.isClosed}
        className={triggerClassString.trim()}
        onClick={handleCollapsibleTriggerClick}
        onKeyDown={event => {
          const { key } = event

          if (key === " " || key === "Enter") {
            handleCollapsibleTriggerClick(event)
          }
        }}
        role={TriggerElement.toLowerCase() === "button" ? undefined : "button"}
        style={triggerStyle}
        tabIndex={tabIndex}
        {...triggerElementProps}
      >
        {activeTrigger}
      </TriggerElement>

      {renderTriggerSibling(triggerSibling, classParentString)}

      <div
        aria-hidden={state.isClosed}
        aria-labelledby={triggerIdRef.current}
        className={outerClassString.trim()}
        hidden={isContentHidden}
        id={contentIdRef.current}
        onTransitionEnd={handleTransitionEnd}
        ref={innerRef}
        role="region"
        style={dropdownStyle}
      >
        <div className={innerClassString.trim()}>{renderedChildren}</div>
      </div>
    </ContentContainerElement>
  )
}

const SimpleCollapsible = ({ children, isOpenAccordian, title }) => {
  const [isOpen, setIsOpen] = useState(Boolean(isOpenAccordian))

  useEffect(() => {
    setIsOpen(Boolean(isOpenAccordian))
  }, [isOpenAccordian])

  return (
    <div className={Styles.Collapsible}>
      <div
        className={`header ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(prevState => !prevState)}
      >
        {title}
      </div>
      {isOpen ? <div className="body">{children}</div> : null}
    </div>
  )
}

const Collapsible = props => {
  if (props.title !== undefined && props.trigger === undefined) {
    return <SimpleCollapsible {...props} />
  }

  return <LibraryCollapsible {...props} />
}

export default Collapsible
